import { GAME_CONFIG } from '../config';

/**
 * API错误类型枚举
 */
export enum LLMErrorType {
  AUTHENTICATION = 'authentication',
  RATE_LIMIT = 'rate_limit',
  SERVER = 'server',
  MODEL = 'model',
  NETWORK = 'network',
  TIMEOUT = 'timeout',
  PARSE = 'parse',
  VALIDATION = 'validation',
  UNKNOWN = 'unknown'
}

/**
 * LLM API错误类
 */
export class LLMError extends Error {
  public type: LLMErrorType;
  public status?: number;
  public retry: boolean;
  public original?: any;

  constructor(message: string, type: LLMErrorType = LLMErrorType.UNKNOWN, status?: number, retry: boolean = true, original?: any) {
    super(message);
    this.name = 'LLMError';
    this.type = type;
    this.status = status;
    this.retry = retry;
    this.original = original;
  }

  /**
   * 获取用户友好的错误消息
   */
  getFriendlyMessage(): string {
    switch (this.type) {
      case LLMErrorType.AUTHENTICATION:
        return '身份验证失败，请确认API密钥配置正确。';
      case LLMErrorType.RATE_LIMIT:
        return '已达到API调用速率限制，请稍后再试。';
      case LLMErrorType.SERVER:
        return '服务器出现错误，请稍后再试。';
      case LLMErrorType.MODEL:
        return '模型处理请求时出错，请尝试其他模型。';
      case LLMErrorType.NETWORK:
        return '网络连接出现问题，请检查您的网络连接。';
      case LLMErrorType.TIMEOUT:
        return '请求超时，请稍后再试。';
      case LLMErrorType.PARSE:
        return 'AI响应解析失败，正在尝试修复。';
      case LLMErrorType.VALIDATION:
        return 'AI响应格式无效，请尝试其他提示或模型。';
      default:
        return this.message || '发生未知错误，请稍后再试。';
    }
  }
}

/**
 * 错误处理工具类
 */
export class ErrorHandler {
  private static debugMode: boolean = GAME_CONFIG.debugMode;

  /**
   * 设置调试模式
   */
  static setDebugMode(debugMode: boolean): void {
    ErrorHandler.debugMode = debugMode;
  }

  /**
   * 处理API响应错误
   */
  static async handleApiError(response: Response, source?: string): Promise<LLMError> {
    let errorText = '';
    let errorData: any = null;
    
    try {
      errorText = await response.text();
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        // 忽略解析错误
      }
    } catch (e) {
      errorText = `无法获取错误详情: ${e}`;
    }

    const status = response.status;
    const errorType = this.determineErrorType(status, errorData, errorText);
    const shouldRetry = this.shouldRetryError(errorType, status);
    
    const error = new LLMError(
      this.formatErrorMessage(status, errorText, source),
      errorType,
      status,
      shouldRetry,
      { errorData, errorText }
    );

    if (this.debugMode) {
      console.error(`API错误 (${source || 'unknown'})`, {
        status,
        type: errorType,
        message: error.message,
        errorText,
        errorData,
        shouldRetry
      });
    }

    return error;
  }

  /**
   * 处理一般错误
   */
  static handleError(error: any, source?: string): LLMError {
    if (error instanceof LLMError) {
      return error;
    }

    const isNetworkError = error instanceof TypeError && 
      (error.message.includes('network') || error.message.includes('连接') || error.message.includes('fetch'));
    
    const errorType = isNetworkError ? LLMErrorType.NETWORK : LLMErrorType.UNKNOWN;
    const shouldRetry = this.shouldRetryError(errorType);
    
    const llmError = new LLMError(
      error.message || '未知错误',
      errorType,
      undefined,
      shouldRetry,
      error
    );

    if (this.debugMode) {
      console.error(`错误捕获 (${source || 'unknown'})`, {
        type: errorType,
        message: llmError.message,
        original: error,
        stack: error.stack,
        shouldRetry
      });
    }

    return llmError;
  }

  /**
   * 确定错误类型
   */
  private static determineErrorType(status: number, errorData: any, errorText: string): LLMErrorType {
    if (status === 401 || status === 403) {
      return LLMErrorType.AUTHENTICATION;
    } else if (status === 429) {
      return LLMErrorType.RATE_LIMIT;
    } else if (status >= 500) {
      return LLMErrorType.SERVER;
    } else if (status === 404) {
      // 可能是模型不存在
      if (errorText.includes('model') || (errorData && errorData.error && errorData.error.includes('model'))) {
        return LLMErrorType.MODEL;
      }
      return LLMErrorType.SERVER;
    } else if (status === 408) {
      return LLMErrorType.TIMEOUT;
    } else {
      return LLMErrorType.UNKNOWN;
    }
  }

  /**
   * 判断是否应该重试错误
   */
  private static shouldRetryError(type: LLMErrorType, status?: number): boolean {
    switch (type) {
      case LLMErrorType.AUTHENTICATION:
        return false; // 认证错误无法通过重试解决
      case LLMErrorType.RATE_LIMIT:
        return true;  // 速率限制可以通过重试解决
      case LLMErrorType.SERVER:
        return true;  // 服务器错误可能是临时的
      case LLMErrorType.MODEL:
        return false; // 模型错误一般无法通过重试解决
      case LLMErrorType.NETWORK:
        return true;  // 网络错误可能是临时的
      case LLMErrorType.TIMEOUT:
        return true;  // 超时可以重试
      case LLMErrorType.PARSE:
      case LLMErrorType.VALIDATION:
        return false; // 解析和验证错误一般需要修改请求
      default:
        // 4xx错误一般不需要重试，5xx错误可能需要
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return true;
    }
  }

  /**
   * 格式化错误消息
   */
  private static formatErrorMessage(status: number, errorText: string, source?: string): string {
    let message = `API调用失败 (${status})`;
    
    if (source) {
      message += ` [${source}]`;
    }
    
    if (errorText) {
      try {
        const errorData = JSON.parse(errorText);
        if (errorData.error && typeof errorData.error === 'string') {
          message += `: ${errorData.error}`;
        } else if (errorData.message && typeof errorData.message === 'string') {
          message += `: ${errorData.message}`;
        } else {
          message += `: ${errorText.substring(0, 100)}`;
        }
      } catch (e) {
        message += `: ${errorText.substring(0, 100)}`;
      }
    }
    
    return message;
  }

  /**
   * 创建重试延迟函数
   */
  static createBackoffDelay(retryCount: number, options = { base: 1000, max: 10000, jitter: true }): number {
    // 指数退避算法: base * 2^retryCount
    let delay = Math.min(options.base * Math.pow(2, retryCount), options.max);
    
    // 添加随机抖动，避免多个请求同时重试
    if (options.jitter) {
      delay = delay * (0.5 + Math.random() * 0.5);
    }
    
    return delay;
  }
} 